import { createContext, type FC, type ReactNode, useContext, useMemo } from "react";
import { type EmojiCache, makeEmojiCache } from "../../../../modules/emoji/emoji-cache";
import type { MomentAnalyticsTotals } from "../../../../modules/stats/server/analytics-service";
import type { UseSupabaseLoaderState } from "../../../../server/supabase/hooks/types";
import { propertyNotEmpty } from "../../../../utils/not-empty";

type MomentStatsTotalsContextType = {
  emojiCache: EmojiCache;

  totals?: UseSupabaseLoaderState<MomentAnalyticsTotals>;
};

const MomentAnalyticsContext = createContext<MomentStatsTotalsContextType | null>(null);

type MomentAnalyticsProviderProps = Omit<MomentStatsTotalsContextType, "emojiCache"> & {
  children: ReactNode;
};

export const MomentAnalyticsProvider: FC<MomentAnalyticsProviderProps> = ({ totals, children }) => {
  const emojiCache = useMemo(() => {
    if (!totals || totals.loading || totals.error) return {};

    const emojis = Object.entries(totals.data.reaction_summary).map(([emoji, { custom_url }]) => ({
      emoji,
      custom_url,
    }));

    return makeEmojiCache(
      emojis.map(({ emoji }) => emoji),
      Object.fromEntries(
        emojis
          .filter(propertyNotEmpty("custom_url"))
          .map(({ emoji, custom_url }) => [emoji, custom_url]),
      ),
    );
  }, [totals]);

  return (
    <MomentAnalyticsContext.Provider value={{ totals, emojiCache }}>
      {children}
    </MomentAnalyticsContext.Provider>
  );
};

export const useMomentAnalyticsContext = (): MomentStatsTotalsContextType => {
  const context = useContext(MomentAnalyticsContext);
  if (!context) {
    throw new Error("useMomentAnalyticsContext be used within a MomentAnalyticsProvider");
  }
  return context;
};
